/**
 * EditRuleDialog Component
 *
 * Dialog for editing an existing learned rule
 * - Shows current rule data
 * - Allows editing accounts, VAT, and settings
 * - Shows performance metrics
 * - Pattern type and key are read-only to maintain integrity
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LearningRulesService } from '@/services/learningRulesService';
import { useToast } from '@/hooks/use-toast';
import type { LearnedRule, UpdateRuleRequest } from '@/types/learningRules';
import { Loader2, Info, TrendingUp, AlertTriangle } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface EditRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule: LearnedRule | null;
  mandateName?: string;
  onSuccess?: () => void;
}

export const EditRuleDialog: React.FC<EditRuleDialogProps> = ({
  open,
  onOpenChange,
  rule,
  mandateName,
  onSuccess,
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  // Form state
  const [debitAccount, setDebitAccount] = useState('');
  const [creditAccount, setCreditAccount] = useState('');
  const [vatCode, setVatCode] = useState('');
  const [vatAccount, setVatAccount] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isShared, setIsShared] = useState(false);

  // Load rule data when dialog opens
  useEffect(() => {
    if (rule) {
      setDebitAccount(rule.suggested_debit_account);
      setCreditAccount(rule.suggested_credit_account);
      setVatCode(rule.vat_code || '');
      setVatAccount(rule.vat_account || '');
      setIsActive(rule.is_active);
      setIsShared(rule.is_shared);
    }
  }, [rule]);

  if (!rule) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!debitAccount.trim() || !creditAccount.trim()) {
      toast({
        title: 'Fehler',
        description: 'Debit und Credit Konten sind erforderlich.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const updates: UpdateRuleRequest = {
        suggested_debit_account: debitAccount.trim(),
        suggested_credit_account: creditAccount.trim(),
        vat_code: vatCode.trim() || undefined,
        vat_account: vatAccount.trim() || undefined,
        is_active: isActive,
        is_shared: isShared,
        scope: isShared ? 'organization' : 'user',
      };

      await LearningRulesService.updateRule(rule.id, updates);

      toast({
        title: 'Erfolg',
        description: `Regel "${rule.pattern_key}" wurde aktualisiert.`,
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error updating rule:', error);
      toast({
        title: 'Fehler',
        description: 'Regel konnte nicht aktualisiert werden.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate metrics
  const successRate = rule.usage_count > 0
    ? (rule.success_count / rule.usage_count) * 100
    : 0;

  const confidenceStatus = rule.current_confidence >= 0.9
    ? 'excellent'
    : rule.current_confidence >= 0.75
    ? 'good'
    : 'needs_review';

  const getPatternTypeLabel = () => {
    switch (rule.pattern_type) {
      case 'counterparty': return 'Counterparty';
      case 'description_pattern': return 'Description';
      case 'amount_based': return 'Amount-Based';
      case 'semantic': return 'Semantic';
      default: return rule.pattern_type;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Regel bearbeiten</DialogTitle>
          <DialogDescription>
            Bearbeite die Klassifikationsregel für{' '}
            {mandateName && <Badge className="ml-1">{mandateName}</Badge>}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Performance Metrics */}
          <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
            <div>
              <div className="text-xs text-muted-foreground">Confidence</div>
              <div className="text-2xl font-bold mt-1">
                {(rule.current_confidence * 100).toFixed(0)}%
              </div>
              <Badge
                variant={
                  confidenceStatus === 'excellent'
                    ? 'default'
                    : confidenceStatus === 'good'
                    ? 'secondary'
                    : 'destructive'
                }
                className="mt-1"
              >
                {confidenceStatus === 'excellent' && '🟢 Excellent'}
                {confidenceStatus === 'good' && '🟡 Good'}
                {confidenceStatus === 'needs_review' && '🔴 Needs Review'}
              </Badge>
            </div>

            <div>
              <div className="text-xs text-muted-foreground">Success Rate</div>
              <div className="text-2xl font-bold mt-1">
                {successRate.toFixed(0)}%
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {rule.success_count}/{rule.usage_count} successful
              </div>
            </div>

            <div>
              <div className="text-xs text-muted-foreground">Usage</div>
              <div className="text-2xl font-bold mt-1">{rule.usage_count}x</div>
              {rule.correction_count > 0 && (
                <div className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {rule.correction_count} corrections
                </div>
              )}
            </div>
          </div>

          {/* Deprecation Warning */}
          {rule.is_deprecated && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Deprecated:</strong> {rule.deprecated_reason || 'Diese Regel ist veraltet.'}
              </AlertDescription>
            </Alert>
          )}

          {/* Low Performance Warning */}
          {confidenceStatus === 'needs_review' && !rule.is_deprecated && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Diese Regel hat eine niedrige Confidence und sollte überprüft werden.
                Möglicherweise ist eine Anpassung erforderlich.
              </AlertDescription>
            </Alert>
          )}

          {/* Read-only Pattern Information */}
          <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
            <h4 className="text-sm font-medium">Pattern Information (Read-only)</h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pattern Type</Label>
                <div className="text-sm font-mono">
                  <Badge variant="outline">{getPatternTypeLabel()}</Badge>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Pattern Key</Label>
                <div className="text-sm font-semibold">{rule.pattern_key}</div>
              </div>
            </div>

            {rule.pattern_value && (
              <div className="space-y-2">
                <Label>Pattern Value</Label>
                <div className="text-sm">{rule.pattern_value}</div>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Pattern Informationen können nicht geändert werden, um die Integrität
              der Regel zu bewahren.
            </p>
          </div>

          <Separator />

          {/* Editable Account Mapping */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">Kontenzuordnung</h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="debitAccount">
                  Debit Konto <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="debitAccount"
                  placeholder="z.B. 6500"
                  value={debitAccount}
                  onChange={(e) => setDebitAccount(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="creditAccount">
                  Credit Konto <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="creditAccount"
                  placeholder="z.B. 1020"
                  value={creditAccount}
                  onChange={(e) => setCreditAccount(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          {/* VAT Configuration */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">MWST Konfiguration</h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vatCode">MWST Code</Label>
                <Input
                  id="vatCode"
                  placeholder="z.B. V77, VR77"
                  value={vatCode}
                  onChange={(e) => setVatCode(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vatAccount">MWST Konto</Label>
                <Input
                  id="vatAccount"
                  placeholder="z.B. 2200"
                  value={vatAccount}
                  onChange={(e) => setVatAccount(e.target.value)}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Rule Status */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">Status & Einstellungen</h4>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="isActive"
                checked={isActive}
                onCheckedChange={(checked) => setIsActive(checked as boolean)}
              />
              <Label
                htmlFor="isActive"
                className="text-sm font-normal cursor-pointer"
              >
                Regel ist aktiv
              </Label>
            </div>

            {!isActive && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Inaktive Regeln werden nicht auf neue Transaktionen angewendet.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex items-center space-x-2">
              <Checkbox
                id="isShared"
                checked={isShared}
                onCheckedChange={(checked) => setIsShared(checked as boolean)}
              />
              <Label
                htmlFor="isShared"
                className="text-sm font-normal cursor-pointer"
              >
                Mit Organisation teilen
              </Label>
            </div>

            {isShared && (
              <Alert>
                <TrendingUp className="h-4 w-4" />
                <AlertDescription>
                  Diese Regel ist für alle Benutzer in Ihrer Organisation verfügbar.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird gespeichert...
                </>
              ) : (
                'Änderungen speichern'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditRuleDialog;
